import { useAuthStore } from '@/store/auth';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

/**
 * Teto de espera de uma LEITURA. `fetch` não tem timeout: numa rede móvel que
 * oscila, a request fica pendurada pra sempre — não resolve nem rejeita — e a
 * tela que a espera congela sem erro e sem botão de retry. Foi assim que a
 * Home ficou no skeleton eterno pro cliente de 2026-09-05 ("o app está fora
 * do ar", com o servidor 100% no ar e todas as chamadas do dia em 200).
 * Erro de verdade é melhor que tela morta.
 */
const READ_TIMEOUT_MS = 20_000;

/**
 * ESCRITA ganha folga bem maior, por dois motivos: abortar no cliente não
 * desfaz nada no servidor (o POST pode ter sido aplicado e o app não saberia),
 * e existe mutação legitimamente lenta — cartão no PagBank
 * (`pagbank_card_timeout_seconds` = 30s) e RemoteStart no Hub
 * (`hub_request_timeout_seconds` = 10s). Cortar antes disso viraria sessão
 * órfã / cobrança fantasma, que é exatamente o que a §3.8 do CLAUDE.md manda
 * evitar. Quem precisar de outro teto passa `timeoutMs`.
 */
const WRITE_TIMEOUT_MS = 45_000;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  cache?: RequestCache;
  /** Sobrescreve o teto padrão (leitura 20s, escrita 45s). */
  timeoutMs?: number;
}

/**
 * Falha ANTES de existir resposta HTTP (timeout, DNS, aparelho offline). Vira
 * `ApiError` com `status: 0` pra que todo caller que já trata `ApiError` —
 * incluindo os `catch` que caem em "Falha ao carregar" — mostre um estado de
 * erro em vez de ficar esperando pra sempre.
 */
function networkError(err: unknown, gotResponse: boolean): ApiError {
  if ((err as { name?: string } | null)?.name === 'AbortError') {
    return new ApiError(
      0,
      'network_timeout',
      'O servidor demorou pra responder. Verifique sua conexão e tente de novo.'
    );
  }
  if (gotResponse) {
    return new ApiError(0, 'bad_response', 'Resposta inválida do servidor. Tente de novo.');
  }
  return new ApiError(
    0,
    'network_error',
    'Sem conexão com o servidor. Verifique sua internet e tente de novo.'
  );
}

export async function apiRequest<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers ?? {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const method = init.method ?? 'GET';
  const timeoutMs = init.timeoutMs ?? (method === 'GET' ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // Só pra distinguir "não chegou resposta" de "resposta chegou e o corpo é
  // que estava quebrado" na mensagem de erro.
  let gotResponse = false;

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      // App autenticado de dados dinâmicos (saldo, regras, sessões): nunca
      // queremos resposta do HTTP cache do browser. Sem isso, mudanças feitas
      // no admin (ex.: regra de cashback) podem demorar a aparecer no app.
      cache: init.cache ?? 'no-store',
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      signal: controller.signal
    });
    gotResponse = true;

    if (!res.ok) {
      let code = 'unknown';
      let message = res.statusText;
      try {
        const data = await res.json();
        // Backend do Bow usa chave `error` nos BowError (ver main.py
        // exception_handler). FastAPI nativo usa `detail`. Cobrimos os dois.
        code = data.error ?? data.code ?? data.detail?.code ?? code;
        message = data.message ?? data.detail?.message ?? data.detail ?? message;
      } catch {
        // non-JSON error body — manter status text
      }
      if (res.status === 401) {
        useAuthStore.getState().logout();
      }
      throw new ApiError(res.status, code, message);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw networkError(err, gotResponse);
  } finally {
    clearTimeout(timer);
  }
}

export const api = {
  get: <T>(path: string) => apiRequest<T>(path),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' })
};
